import React, { useState, useEffect, useRef } from "react";
import { Sparkles, TrendingUp, Star, X, Share2, Bookmark, Eye } from "lucide-react";
import ShareMenu from "./ShareMenu";
import ResourceSimpleItem from "./ResourceSimpleItem";
import MaterialCover from "./MaterialCover";
import formatCount from "../../utils/formatCount";

const typeBadgeColor = {
  book: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  note: "bg-blue-500/10 text-foreground/80 border-blue-500/20",
  quiz: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  exercise: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const typeLabel = {
  book: "Book", note: "Notes", quiz: "Quiz", exercise: "Exercise",
};

const dismissReasons = ["Not relevant to my major", "Already have this", "Not interested in this topic", "Too advanced / too basic"];

const LibraryRecommendations = ({ recommended = [], trending = [], onOpen, saved, onSave, onDismiss, viewMode = 'list' }) => {
  const [activeDismissKey, setActiveDismissKey] = useState(null);
  const [shareMenuId, setShareMenuId] = useState(null);
  const activeMenuRef = useRef(null);
  const activeOverlayRef = useRef(null);

  // Two genuinely different, independently-fetched sets (highest-rated vs.
  // most-viewed) — not two slices of the same page-1 results, which used to
  // make both sections show the same handful of items.
  const sections = [
    { key: "recommended", title: "Recommended for You", subtitle: "Highest rated academic resources", icon: Sparkles, items: recommended },
    { key: "trending", title: "Trending This Week", subtitle: "Most viewed among students", icon: TrendingUp, items: trending },
  ];

  const closeMenus = () => {
    setShareMenuId(null);
    setActiveDismissKey(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenuRef.current && !activeMenuRef.current.contains(e.target)) {
        setShareMenuId(null);
      }
      if (activeOverlayRef.current && !activeOverlayRef.current.contains(e.target)) {
        setActiveDismissKey(null);
      }
    };
    if (shareMenuId || activeDismissKey) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [shareMenuId, activeDismissKey]);

  return (
    <div className="space-y-12">
      {sections.map((s) => {
        if (!s.items || s.items.length === 0) return null;
        return (
          <div key={s.key}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="section-title text-base flex items-center gap-2 mt-0">
                  <s.icon className="w-4 h-4 text-primary" /> {s.title}
                </h3>
                <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground/40">{s.subtitle}</p>
              </div>
            </div>

          {viewMode === 'list' ? (
            <div className="flex flex-col gap-2">
                {s.items.map((r) => {
                  const cardKey = `${s.key}-${r.id}`;
                  const isSharing = shareMenuId === cardKey;
                  
                  return (
                    <div key={cardKey} className="relative">
                      <ResourceSimpleItem
                          item={r}
                          isSaved={saved.includes(r.id)}
                          onOpen={onOpen}
                          onSave={onSave}
                          onShare={() => setShareMenuId(cardKey)}
                          onDismiss={() => setActiveDismissKey(cardKey)}
                      />
                      {isSharing && (
                        <div ref={activeMenuRef} className="absolute top-1/2 -translate-y-1/2 right-[120px] z-[100] animate-in fade-in zoom-in-95 slide-in-from-right-4 duration-300 ease-out">
                          <ShareMenu
                            resourceId={r.id}
                            resourceTitle={r.title}
                            onClose={() => setShareMenuId(null)}
                            position="left"
                          />
                        </div>
                      )}
                      {activeDismissKey === cardKey && (
                        <div className="absolute inset-0 z-[100] flex items-center justify-center animate-in fade-in duration-200">
                          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm rounded-xl" />
                          <div ref={activeOverlayRef} className="relative bg-card rounded-2xl shadow-2xl border border-border/20 p-2 flex items-center gap-2 max-w-[90%] overflow-x-auto">
                            <span className="text-[10px] font-bold text-muted-foreground/60 px-2 shrink-0">Remove:</span>
                            {dismissReasons.map((reason) => (
                              <button
                                key={reason}
                                onClick={() => { onDismiss?.(r.id); setActiveDismissKey(null); }}
                                className="whitespace-nowrap px-3 py-1.5 text-[10px] font-bold text-muted-foreground hover:bg-muted hover:text-red-500 rounded-lg transition-all border border-border/50"
                              >
                                {reason}
                              </button>
                            ))}
                            <button 
                              onClick={() => setActiveDismissKey(null)}
                              className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-muted-foreground/30 hover:text-foreground transition-colors shrink-0"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {s.items.map((r) => {
              const cardKey = `${s.key}-${r.id}`;
              const isDismissing = activeDismissKey === cardKey;
              
              return (
              <div 
                key={cardKey} 
                className={`group relative bg-card rounded-2xl transition-all duration-500 hover:-translate-y-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(15,23,42,0.1)] border border-border ${(shareMenuId === cardKey || isDismissing) ? 'z-[100]' : 'z-10'}`}
              >
                {r.hasNew && (
                  <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse border-2 border-background shadow-lg shadow-red-500/40" />
                    <span className="text-[10px] font-black text-red-500 uppercase tracking-widest bg-card/90 backdrop-blur-xl px-2 py-0.5 rounded-lg border border-red-500/10">Hot</span>
                  </div>
                )}

                {onDismiss && s.key !== "following" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveDismissKey(isDismissing ? null : cardKey); }}
                    className={`absolute top-4 right-4 z-[40] w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-500 scale-90 group-hover:scale-100 opacity-0 group-hover:opacity-100 shadow-xl ${
                      isDismissing ? 'bg-destructive text-destructive-foreground' : 'bg-card/90 backdrop-blur-2xl border border-border/60 text-muted-foreground/60 hover:text-red-500 hover:bg-red-50'
                    }`}
                  >
                    <X className="w-5 h-5 flex-shrink-0" />
                  </button>
                )}

                {isDismissing && (
                  <div className="absolute inset-0 z-[100] flex items-center justify-center p-3 animate-in fade-in zoom-in-95 duration-200">
                    <div className="absolute inset-0 bg-background/60 backdrop-blur-md rounded-2xl" />
                    <div ref={activeOverlayRef} className="relative bg-card rounded-3xl shadow-[0_40px_100px_rgba(0,0,0,0.4)] border border-border/20 overflow-hidden w-full max-w-[260px] animate-in fade-in zoom-in-90 slide-in-from-bottom-4 duration-300 ease-out">
                      <div className="px-6 pt-8 pb-3 text-center">
                        <h5 className="text-[13px] font-black text-foreground uppercase tracking-wider mb-1">Remove Tip</h5>
                        <p className="text-[11px] text-muted-foreground/60 font-bold leading-relaxed">Why shouldn't we show this?</p>
                      </div>
                      <div className="p-2 flex flex-col gap-1">
                        {dismissReasons.map((reason) => (
                          <button
                            key={reason}
                            onClick={() => { onDismiss?.(r.id); setActiveDismissKey(null); }}
                            className="w-full text-left px-5 py-3 text-[11px] font-bold text-muted-foreground hover:bg-muted hover:text-red-500 rounded-xl transition-all"
                          >
                            {reason}
                          </button>
                        ))}
                      </div>
                      <button 
                        onClick={() => setActiveDismissKey(null)}
                        className="w-full py-4 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 border-t border-border/30 hover:text-foreground transition-colors"
                      >
                        Nevermind
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => onOpen(r.id)}
                  className="w-full aspect-[16/9] relative overflow-hidden border-none p-0 bg-transparent cursor-pointer"
                >
                  <MaterialCover item={r} iconSize={40} hover />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[8px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider border ${typeBadgeColor[r.type] || 'bg-muted/30 text-muted-foreground/80'}`}>{typeLabel[r.type]}</span>
                    <span className="flex items-center gap-1 text-[11px] font-bold text-muted-foreground/80 ml-auto">
                      <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> {r.rating}
                    </span>
                  </div>
                  <button onClick={() => onOpen(r.id)} className="block text-left w-full mb-2">
                    <p className="text-[13px] font-bold text-foreground group-hover:text-primary transition-colors truncate">{r.title}</p>
                  </button>
                  <div className="flex items-center gap-1 text-[9px] text-muted-foreground/60 font-bold uppercase tracking-wider mb-3">
                    <Eye className="w-3 h-3" /> <span title={`${r.views || 0} views`}>{formatCount(r.views)}</span>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-border/30">
                    <button
                      onClick={() => onOpen(r.id)}
                      className="flex-1 h-8 rounded-lg bg-primary text-primary-foreground text-[10px] font-bold hover:bg-primary/90 transition-all active:scale-95"
                    >
                      Open
                    </button>
                    <button
                      onClick={() => onSave?.(r.id)}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all border ${
                        saved.includes(r.id) ? "bg-primary border-primary text-primary-foreground" : "bg-card text-muted-foreground/60 border-border/50 hover:text-foreground hover:border-border"
                      }`}
                      title={saved.includes(r.id) ? "Remove from My Notes" : "Save to My Notes"}
                    >
                      <Bookmark className={`w-3.5 h-3.5 ${saved.includes(r.id) ? "fill-current" : ""}`} />
                    </button>
                  </div>

                  {/* Share button in top-left or overlay */}
                  <div className="absolute top-4 left-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-700 -translate-x-4 group-hover:translate-x-0 z-[40]">
                    <div className="relative">
                      <button
                        onClick={() => { closeMenus(); setShareMenuId(shareMenuId === cardKey ? null : cardKey); }}
                        className={`w-10 h-10 rounded-2xl transition-all shadow-[0_10px_30px_rgba(0,0,0,0.08)] border flex items-center justify-center hover:scale-110 active:scale-95 group/btn-action ${
                          shareMenuId === cardKey ? "bg-primary text-primary-foreground border-primary" : "bg-card/95 backdrop-blur-2xl text-muted-foreground/80 border-border/60 hover:text-primary"
                        }`}
                        title="Share"
                      >
                        <Share2 className="w-4 h-4 transition-transform group-active/btn-action:scale-90" />
                      </button>
                      {shareMenuId === cardKey && (
                        <div ref={activeMenuRef} className="absolute top-1/2 -translate-y-1/2 left-full ml-4 z-[100] animate-in fade-in zoom-in-95 slide-in-from-left-4 duration-300 ease-out">
                          <ShareMenu
                            resourceId={r.id}
                            resourceTitle={r.title}
                            onClose={() => setShareMenuId(null)}
                            position="right"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default LibraryRecommendations;
